import { selectIsRokuTemplatingFeatureFlagOn } from 'core/userContext';
import { AppPlatform } from 'modules/mobileApps/types';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { PrimaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { TileRadio } from 'ui/radio/TileRadio';
import { colors, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { AppIcon } from '../AppIcon';
import { MobileAppList } from '../AppsAndWebsites/styles';
import {
  getPackageLabel,
  getStoreURLPlaceholder,
  isInvalidInput,
  validatePackageName,
} from '../utils';

export const FORM_DATA_TEST_ID = 'mobileApps-MobileAppForm';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: max(50%, ${pxToRem(400)});
  padding-top: ${spacing.double};
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  margin-left: auto;
  margin-top: ${spacing.base};
`;

const PlatformLabel = styled.div<{ color: string }>`
  color: ${({ color }) => color};
`;

type Props = {
  handleSubmit: () => void;
  mode?: string;
  name: string;
  onNameChange: (value: string) => void;
  onPackageNameChange: (value: string) => void;
  onPlatformChange: (value: AppPlatform) => void;
  onStoreUrlChange: (value: string) => void;
  packageName: string;
  platform?: AppPlatform;
  storeUrl: string;
  submitButtonText: string;
};

export const ConfigureAppForm = ({
  handleSubmit,
  mode = 'create',
  name = '',
  onNameChange,
  onPackageNameChange,
  onPlatformChange,
  onStoreUrlChange,
  packageName = '',
  platform,
  storeUrl = '',
  submitButtonText = 'Submit',
}: Props) => {
  const [selectedPlatform, setSelectedPlatform] = useState('');

  // error messages returned here are an array of errors
  const errorMessages = validatePackageName(platform, packageName);
  const hasError = errorMessages.length > 0;
  const errorMessageToDisplay = errorMessages.join(' ');

  // when editing the info of an app,
  // a user should not be able to change its platform type or package name
  const isDisabled = mode === 'edit';
  const platformSelectionColor = isDisabled ? colors.grey35 : colors.black;

  const isRokuTemplatingOn = useSelector(selectIsRokuTemplatingFeatureFlagOn);
  const mobileAppListChoices = [
    AppPlatform.IOS,
    AppPlatform.ANDROID,
    AppPlatform.WEB,
    ...(isRokuTemplatingOn ? [AppPlatform.OTT] : []),
  ];

  return (
    <Container data-test={FORM_DATA_TEST_ID}>
      <FormField inputId="name" labelText="Name" hasError={false}>
        <InputText
          data-test="mobileApps-MobileAppForm-NameInput"
          id="name"
          onChange={onNameChange}
          placeholder="Your App or Website"
          value={name}
        />
      </FormField>
      <FormField labelText="Platform" inputId="platform" hasError={false}>
        <MobileAppList data-test="form-field-mobileApp-platform" data-formview={isRokuTemplatingOn}>
          {mobileAppListChoices.map((platformType) => (
            <TileRadio
              id={`form-field-mobileApp-platform-${platformType}`}
              data-test={`form-field-mobileApp-platform-${platformType}`}
              key={platformType}
              isDisabled={isDisabled}
              isChecked={platform === platformType}
              value={selectedPlatform}
              onClick={() => {
                if (!isDisabled) {
                  onPlatformChange(platformType);
                  setSelectedPlatform(platformType);
                }
              }}
            >
              <PlatformLabel color={platformSelectionColor}>
                {platformType === AppPlatform.WEB ? 'Website' : platformType}
              </PlatformLabel>
              <AppIcon platformType={platformType} width={16} color={platformSelectionColor} />
            </TileRadio>
          ))}
        </MobileAppList>
      </FormField>
      <FormField
        data-test="form-field-mobileApp-packageName"
        inputId="packageName"
        labelText={getPackageLabel(platform)}
        hasError={hasError}
        errorText={errorMessageToDisplay}
      >
        <InputText
          id="packageName"
          onChange={onPackageNameChange}
          data-test="mobileApps-MobileAppForm-PackageName"
          placeholder={platform === AppPlatform.WEB ? 'www.your-website.com' : 'com.yourApp.app'}
          value={packageName}
          isDisabled={isDisabled}
        />
      </FormField>
      {platform !== AppPlatform.WEB && platform !== AppPlatform.OTT && (
        <FormField
          labelText="Store URL"
          inputId="storeUrl"
          hasError={false}
          data-test="form-field-mobileApp-StoreUrlInput"
        >
          <InputText
            id="storeUrl"
            data-test="mobileApps-MobileAppForm-StoreUrlInput"
            onChange={onStoreUrlChange}
            placeholder={getStoreURLPlaceholder(platform)}
            value={storeUrl}
          />
        </FormField>
      )}
      <StyledPrimaryButton
        isDisabled={isInvalidInput(name, packageName, platform, hasError)}
        onClick={handleSubmit}
        data-test="mobileApps-MobileAppFormSaveButton"
      >
        {submitButtonText}
      </StyledPrimaryButton>
    </Container>
  );
};
